import { useState } from "react";
import classNames from "classnames";
import { get, has, isEmpty, isNil } from "lodash";
import PropTypes from "prop-types";
import {
  BiCheck,
  BiDownload,
  BiError,
  BiIdCard,
  BiKey,
  BiLinkExternal,
  BiPlug,
  BiPlus,
} from "react-icons/bi";
import { base64Of, saveBytes } from "utils/helpers/bytes";
import { formatLocalDate, formatLocalDateTime } from "utils/helpers/date";
import { fill } from "utils/helpers/template";
import {
  Choice,
  CHOICE_TONES,
  CodeBlock,
  CopyButton,
  CREATORS,
  CreatorPicker,
  EMAIL_FORMAT,
  KEY_METHODS,
  MethodPicker,
  Recipient,
  SEED_METHODS,
  SHELL_LINKS,
  Step,
} from "./EncryptionKey";
import ExpiryPicker, { expiryDateOf, isExpiryValid } from "./ExpiryPicker";
import Segmented, { SEGMENTED_VARIANTS } from "./Segmented";
import { API_CREDENTIAL_METHODS } from "./setup";

const PUBLIC_KEY_FILE = "kayros-public-key.pem";

const KEY_COMMANDS = [
  "openssl genpkey -algorithm RSA \\",
  "  -pkeyopt rsa_keygen_bits:2048 \\",
  "  -out kayros-private-key.pem",
  "",
  "openssl pkey -in kayros-private-key.pem \\",
  `  -pubout -out ${PUBLIC_KEY_FILE}`,
].join("\n");

const KEY_CLEANUP = `rm kayros-private-key.pem ${PUBLIC_KEY_FILE}`;

const RSA_KEY = {
  name: "RSASSA-PKCS1-v1_5",
  modulusLength: 2048,
  publicExponent: new Uint8Array([1, 0, 1]),
  hash: "SHA-256",
};

const CHOOSE = "CHOOSE";
const YEAR = 365 * 24 * 60 * 60 * 1000;
const EXPIRY_CHOICES = [true, false];

const inOneYear = () => new Date(Date.now() + YEAR).toISOString().slice(0, 10);

const pemOf = (bytes, label) =>
  `-----BEGIN ${label}-----\n${base64Of(bytes)
    .match(/.{1,64}/g)
    .join("\n")}\n-----END ${label}-----\n`;

const derLength = (bytes, offset) => {
  const first = bytes[offset];
  if (first < 0x80) return { length: first, start: offset + 1 };
  const end = offset + 1 + (first - 0x80);
  return {
    length: bytes
      .slice(offset + 1, end)
      .reduce((total, byte) => total * 256 + byte, 0),
    start: end,
  };
};

const nextElement = (bytes, offset) => {
  const { length, start } = derLength(bytes, offset + 1);
  return start + length;
};

const rsaPrivateKeyOf = (pkcs8) => {
  const version = derLength(pkcs8, 1).start;
  const algorithm = nextElement(pkcs8, version);
  const privateKey = nextElement(pkcs8, algorithm);
  const octets = derLength(pkcs8, privateKey + 1);
  return pkcs8.slice(octets.start, octets.start + octets.length);
};

const generatePair = () =>
  window.crypto.subtle
    .generateKey(RSA_KEY, true, ["sign", "verify"])
    .then(({ publicKey, privateKey }) =>
      Promise.all([
        window.crypto.subtle.exportKey("spki", publicKey),
        window.crypto.subtle.exportKey("pkcs8", privateKey),
      ]),
    )
    .then(([spki, pkcs8]) => ({
      publicKey: pemOf(new Uint8Array(spki), "PUBLIC KEY"),
      privateKey: pemOf(
        rsaPrivateKeyOf(new Uint8Array(pkcs8)),
        "RSA PRIVATE KEY",
      ),
    }));

const keyFileNameOf = (credential) => `kayros-api-key-${credential.keyId}.json`;

export const saveKeyFile = ({ credential, privateKey }) =>
  saveBytes(
    JSON.stringify(
      {
        type: "serviceaccount",
        keyId: credential.keyId,
        key: privateKey,
        userId: credential.userId,
      },
      null,
      2,
    ),
    keyFileNameOf(credential),
  );

const ipsOf = (text) =>
  text
    .split(/[\s,;]+/)
    .map((ip) => ip.trim())
    .filter(Boolean);

const tokenCommandOf = ({ access, credential }) =>
  credential.method === API_CREDENTIAL_METHODS.PRIVATE_KEY
    ? [
        `curl -X POST ${access.tokenUrl} \\`,
        "  -d grant_type=urn:ietf:params:oauth:grant-type:jwt-bearer \\",
        "  -d assertion=<JWT> \\",
        `  --data-urlencode "scope=${access.scope}"`,
      ].join("\n")
    : [
        `curl -X POST ${access.tokenUrl} \\`,
        "  -d grant_type=client_credentials \\",
        `  -d client_id=${credential.clientId} \\`,
        "  -d client_secret=<secret> \\",
        `  --data-urlencode "scope=${access.scope}"`,
      ].join("\n");

const identifierOf = (credential) =>
  credential.method === API_CREDENTIAL_METHODS.CLIENT_SECRET
    ? credential.clientId
    : [credential.userId, credential.keyId].filter(Boolean).join(" · ");

const Value = ({ label, value, labels }) => (
  <div className="onboarding__identity">
    <span className="onboarding__identity-text">
      <span>{label}</span>
      <code>{value}</code>
    </span>
    <CopyButton text={value || ""} labels={labels} />
  </div>
);

Value.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  labels: PropTypes.object.isRequired,
};

Value.defaultProps = {
  value: "",
};

const Connection = ({ access, labels }) => (
  <div className="onboarding__stack">
    <Value label={labels.TOKEN_URL} value={access.tokenUrl} labels={labels} />
    <Value label={labels.SCOPE} value={access.scope} labels={labels} />
    <Value label={labels.AUDIENCE} value={access.issuer} labels={labels} />
    <Value label={labels.API_URL} value={access.apiUrl} labels={labels} />
  </div>
);

Connection.propTypes = {
  access: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
};

const handoverLineOf = ({ handover }, labels) => {
  if (isNil(handover)) return null;
  return handover.open
    ? fill(labels.LINK_SENT, {
        email: handover.email,
        date: formatLocalDateTime(handover.expiresAt),
      })
    : fill(labels.LINK_EXPIRED, { email: handover.email });
};

const CredentialRow = ({ credential, labels, busy, onDelete, onResend }) => {
  const ROW = labels.ROW;
  const { pending, handover } = credential;
  const meta = [
    fill(ROW.CREATED, { date: formatLocalDateTime(credential.createdDate) }),
    pending ? handoverLineOf({ handover }, ROW) : null,
    !pending && credential.lastUsedAt
      ? fill(ROW.LAST_USED, {
          date: formatLocalDateTime(credential.lastUsedAt),
        })
      : null,
    !pending && !credential.lastUsedAt ? ROW.NEVER_USED : null,
    credential.keyExpiresAt
      ? fill(ROW.EXPIRES, { date: formatLocalDate(credential.keyExpiresAt) })
      : null,
    isEmpty(credential.allowedIps)
      ? null
      : fill(ROW.IPS, { ips: credential.allowedIps.join(", ") }),
  ].filter(Boolean);
  return (
    <li className="onboarding__credential">
      <span className="onboarding__credential-text">
        <span className="onboarding__credential-title">
          <strong>{credential.label}</strong>
          <span className="onboarding__chip">
            {get(labels.METHODS, credential.method, credential.method)}
          </span>
          {pending ? (
            <span className="onboarding__badge onboarding__badge--todo">
              {ROW.PENDING}
            </span>
          ) : null}
        </span>
        {pending ? null : <code>{identifierOf(credential)}</code>}
        <span>{meta.join(" · ")}</span>
      </span>
      <span className="onboarding__card-actions">
        {pending && !isNil(handover) ? (
          <button
            type="button"
            className="onboarding__button onboarding__button--outline onboarding__button--small"
            disabled={busy}
            onClick={() => onResend(credential, handover.email)}
          >
            {labels.HANDOVER.RESEND}
          </button>
        ) : null}
        <button
          type="button"
          className="onboarding__button onboarding__button--danger onboarding__button--small"
          disabled={busy}
          onClick={() => onDelete(credential)}
        >
          {labels.DELETE.ACTION}
        </button>
      </span>
    </li>
  );
};

CredentialRow.propTypes = {
  credential: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  busy: PropTypes.bool.isRequired,
  onDelete: PropTypes.func.isRequired,
  onResend: PropTypes.func.isRequired,
};

const Choices = ({ labels, onPick }) => (
  <div className="onboarding__choices">
    <Choice
      icon={<BiKey size={20} />}
      tone={CHOICE_TONES.BLUE}
      tag={labels.KEY.TAG}
      title={labels.KEY.TITLE}
      text={labels.KEY.TEXT}
      needs={labels.KEY.NEEDS}
      action={labels.CHOOSE}
      onClick={() => onPick(API_CREDENTIAL_METHODS.PRIVATE_KEY)}
    />
    <Choice
      icon={<BiIdCard size={20} />}
      tone={CHOICE_TONES.AMBER}
      tag={labels.OAUTH.TAG}
      title={labels.OAUTH.TITLE}
      text={labels.OAUTH.TEXT}
      needs={labels.OAUTH.NEEDS}
      action={labels.CHOOSE}
      onClick={() => onPick(API_CREDENTIAL_METHODS.CLIENT_SECRET)}
    />
  </div>
);

Choices.propTypes = {
  labels: PropTypes.object.isRequired,
  onPick: PropTypes.func.isRequired,
};

const KeyCommands = ({ method, labels }) => {
  const FORM = labels.FORM;
  const hosted = has(SHELL_LINKS, method);
  return (
    <>
      <p className="onboarding__paragraph">
        {get(FORM.COMMANDS_TEXTS, method)}
      </p>
      {hosted ? (
        <a
          className="onboarding__button onboarding__button--outline onboarding__button--icon onboarding__start"
          href={SHELL_LINKS[method]}
          target="_blank"
          rel="noopener noreferrer"
        >
          <BiLinkExternal size={16} />
          {get(FORM.OPEN_SHELL, method)}
        </a>
      ) : null}
      <CodeBlock code={KEY_COMMANDS} labels={labels} />
      {hosted ? (
        <>
          <p className="onboarding__paragraph">
            {get(FORM.SHELL_AFTER, method)}
          </p>
          <CodeBlock code={KEY_CLEANUP} labels={labels} />
        </>
      ) : null}
      <p className="onboarding__notice onboarding__notice--warning onboarding__notice--compact">
        <BiError size={18} aria-hidden="true" />
        <span>{FORM.KEEP}</span>
      </p>
    </>
  );
};

KeyCommands.propTypes = {
  method: PropTypes.string.isRequired,
  labels: PropTypes.object.isRequired,
};

const BrowserPair = ({ pair, labels, busy, onPair }) => {
  const FORM = labels.FORM;
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);

  const generate = () => {
    setWorking(true);
    setError(null);
    return generatePair()
      .then(onPair)
      .catch(() => setError(FORM.GENERATE_FAILED))
      .finally(() => setWorking(false));
  };

  return (
    <>
      <button
        type="button"
        className="onboarding__button onboarding__button--outline onboarding__button--icon onboarding__start"
        disabled={busy || working}
        onClick={generate}
      >
        <BiKey size={16} />
        {working ? FORM.GENERATING : FORM.GENERATE}
      </button>
      {pair ? (
        <p className="onboarding__success-line">
          <BiCheck size={16} aria-hidden="true" />
          {FORM.GENERATED}
        </p>
      ) : null}
      {error ? <p className="onboarding__error">{error}</p> : null}
      <p className="onboarding__notice onboarding__notice--warning onboarding__notice--compact">
        <BiError size={18} aria-hidden="true" />
        <span>{FORM.FILE_NOTE}</span>
      </p>
    </>
  );
};

BrowserPair.propTypes = {
  pair: PropTypes.object,
  labels: PropTypes.object.isRequired,
  busy: PropTypes.bool.isRequired,
  onPair: PropTypes.func.isRequired,
};

BrowserPair.defaultProps = {
  pair: null,
};

export const useKeyPair = ({ labels, busy }) => {
  const FORM = labels.FORM;
  const [keyMethod, setKeyMethod] = useState(SEED_METHODS.BROWSER);
  const [pair, setPair] = useState(null);
  const [upload, setUpload] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const browser = keyMethod === SEED_METHODS.BROWSER;

  const pick = ({ target }) => {
    const [picked] = target.files || [];
    target.value = "";
    if (!picked) return null;
    setUploadError(null);
    return picked
      .text()
      .then((publicKey) => setUpload({ name: picked.name, publicKey }))
      .catch(() => setUploadError(FORM.UPLOAD_FAILED));
  };

  const methodStep = {
    key: "METHOD",
    title: FORM.STEP_METHOD,
    body: (
      <MethodPicker
        method={keyMethod}
        methods={KEY_METHODS}
        labels={FORM}
        reimport={false}
        onChange={setKeyMethod}
      />
    ),
  };

  const sourceSteps = browser
    ? [
        {
          key: "GENERATE",
          title: FORM.STEP_GENERATE,
          body: (
            <BrowserPair
              pair={pair}
              labels={labels}
              busy={busy}
              onPair={setPair}
            />
          ),
        },
      ]
    : [
        {
          key: "COMMANDS",
          title: FORM.STEP_COMMANDS,
          body: <KeyCommands method={keyMethod} labels={labels} />,
        },
        {
          key: "UPLOAD",
          title: FORM.STEP_UPLOAD,
          body: (
            <>
              <div className="onboarding__logo">
                <span className="onboarding__logo-text">
                  <strong>{PUBLIC_KEY_FILE}</strong>
                  <span>
                    {isNil(upload)
                      ? FORM.UPLOAD_HINT
                      : fill(FORM.UPLOAD_READY, { name: upload.name })}
                  </span>
                </span>
                <label className="onboarding__button onboarding__button--outline onboarding__file-button">
                  {FORM.PICK}
                  <input
                    type="file"
                    accept=".pem,.pub,.txt"
                    className="onboarding__file"
                    onChange={pick}
                  />
                </label>
              </div>
              {uploadError ? (
                <p className="onboarding__error">{uploadError}</p>
              ) : null}
            </>
          ),
        },
      ];

  return {
    publicKey: browser ? get(pair, "publicKey") : get(upload, "publicKey"),
    privateKey: browser ? get(pair, "privateKey") : null,
    steps: [methodStep, ...sourceSteps],
  };
};

const DELIVERIES = { SHOW: "SHOW", EMAIL: "EMAIL" };

const submitLabelOf = ({ emailing }, labels) =>
  emailing ? labels.SUBMIT_SEND : labels.SUBMIT;

const NewCredential = ({
  method,
  labels,
  busy,
  adminEmail,
  onCreate,
  onBack,
  onCreated,
}) => {
  const FORM = labels.FORM;
  const signed = method === API_CREDENTIAL_METHODS.PRIVATE_KEY;
  const [label, setLabel] = useState("");
  const [ips, setIps] = useState("");
  const [delivery, setDelivery] = useState(DELIVERIES.SHOW);
  const [creator, setCreator] = useState(CREATORS.HERE);
  const [recipient, setRecipient] = useState(adminEmail || "");
  const [expiry, setExpiry] = useState({ on: true, date: inOneYear() });
  const keyPair = useKeyPair({ labels, busy });
  const emailing = signed
    ? creator === CREATORS.EMAIL
    : delivery === DELIVERIES.EMAIL;

  const keyReady =
    !signed ||
    ((emailing || !isNil(keyPair.publicKey)) && isExpiryValid(expiry));
  const ready =
    !isEmpty(label.trim()) &&
    keyReady &&
    (!emailing || EMAIL_FORMAT.test(recipient));

  const submit = () =>
    onCreate({
      label: label.trim(),
      method,
      allowedIps: ipsOf(ips),
      ...(signed ? { expiresAt: expiryDateOf(expiry) } : {}),
      ...(signed && !emailing ? { publicKey: keyPair.publicKey } : {}),
      ...(emailing ? { recipient } : {}),
    }).then((created) => {
      if (isNil(created)) return;
      const privateKey = emailing ? null : keyPair.privateKey;
      if (privateKey)
        saveKeyFile({ credential: created.credential, privateKey });
      onCreated({ ...created, privateKey });
    });

  const nameStep = {
    key: "NAME",
    title: FORM.STEP_NAME,
    body: (
      <div className="onboarding__field">
        <label htmlFor="api-credential-label">{FORM.NAME}</label>
        <input
          id="api-credential-label"
          type="text"
          autoComplete="off"
          className="onboarding__input"
          placeholder={FORM.NAME_PLACEHOLDER}
          value={label}
          onChange={({ target }) => setLabel(target.value)}
        />
      </div>
    ),
  };

  const deliveryStep = {
    key: "DELIVERY",
    title: FORM.STEP_DELIVERY,
    body: (
      <>
        <Segmented
          variant={SEGMENTED_VARIANTS.NARROW}
          value={delivery}
          options={Object.values(DELIVERIES).map((option) => ({
            value: option,
            label: get(FORM.DELIVERIES, option),
          }))}
          onChange={setDelivery}
        />
        <p className="onboarding__paragraph">
          {get(FORM.DELIVERY_HINTS, delivery)}
        </p>
      </>
    ),
  };

  const recipientStep = {
    key: "RECIPIENT",
    title: labels.HANDOVER.STEP,
    body: (
      <Recipient
        adminEmail={adminEmail}
        value={recipient}
        labels={labels.HANDOVER}
        onChange={setRecipient}
      />
    ),
  };

  const expiryStep = {
    key: "EXPIRY",
    title: FORM.STEP_EXPIRY,
    body: (
      <ExpiryPicker
        id="api-credential-expiry"
        expiry={expiry}
        labels={FORM}
        choices={EXPIRY_CHOICES}
        onChange={setExpiry}
      />
    ),
  };

  const ipsStep = {
    key: "IPS",
    title: FORM.STEP_IPS,
    body: (
      <div className="onboarding__field">
        <label htmlFor="api-credential-ips">{FORM.IPS}</label>
        <input
          id="api-credential-ips"
          type="text"
          autoComplete="off"
          spellCheck={false}
          className="onboarding__input onboarding__input--mono"
          placeholder={FORM.IPS_PLACEHOLDER}
          value={ips}
          onChange={({ target }) => setIps(target.value)}
        />
        <span className="onboarding__hint">{FORM.IPS_HINT}</span>
      </div>
    ),
  };

  const creatorStep = {
    key: "CREATOR",
    title: FORM.STEP_CREATOR,
    body: <CreatorPicker value={creator} labels={FORM} onChange={setCreator} />,
  };

  const sourceSteps = signed && !emailing ? keyPair.steps : [];

  const steps = [
    nameStep,
    signed ? creatorStep : deliveryStep,
    ...sourceSteps,
    ...(emailing ? [recipientStep] : []),
    ...(signed ? [expiryStep] : []),
    ipsStep,
  ];

  return (
    <div className="onboarding__stack">
      <ol className="onboarding__step-list">
        {steps.map((step, index) => (
          <Step key={step.key} number={index + 1} title={step.title}>
            {step.body}
          </Step>
        ))}
      </ol>
      <div className="onboarding__footer onboarding__footer--flat">
        <button
          type="button"
          className="onboarding__button onboarding__button--outline"
          onClick={onBack}
        >
          {labels.BACK}
        </button>
        <button
          type="button"
          className="onboarding__button onboarding__button--primary onboarding__push"
          disabled={!ready || busy}
          onClick={submit}
        >
          {busy ? FORM.SUBMITTING : submitLabelOf({ emailing }, FORM)}
        </button>
      </div>
    </div>
  );
};

NewCredential.propTypes = {
  method: PropTypes.string.isRequired,
  labels: PropTypes.object.isRequired,
  busy: PropTypes.bool.isRequired,
  adminEmail: PropTypes.string,
  onCreate: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
};

NewCredential.defaultProps = {
  adminEmail: "",
};

const Sent = ({ credential, labels, onDone }) => (
  <div className="onboarding__stack">
    <div className="onboarding__banner onboarding__banner--success">
      <span className="onboarding__banner-icon" aria-hidden="true">
        <BiCheck size={20} />
      </span>
      <span className="onboarding__banner-text">
        <span className="onboarding__banner-title">
          {fill(labels.CREATED.TITLE, { label: credential.label })}
        </span>
        <span>
          {fill(labels.HANDOVER.SENT, {
            email: get(credential, "handover.email", ""),
            date: formatLocalDateTime(get(credential, "handover.expiresAt")),
          })}
        </span>
      </span>
    </div>
    <p className="onboarding__paragraph">{labels.CREATED.SENT_NOTE}</p>
    <div className="onboarding__footer onboarding__footer--flat">
      <button
        type="button"
        className="onboarding__button onboarding__button--primary onboarding__push"
        onClick={onDone}
      >
        {labels.CREATED.DONE}
      </button>
    </div>
  </div>
);

Sent.propTypes = {
  credential: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  onDone: PropTypes.func.isRequired,
};

const warningOf = ({ signed, privateKey }, labels) => {
  if (!signed) return labels.SECRET_ONCE;
  if (!isNil(privateKey)) return labels.HANDOVER;
  return labels.KEPT;
};

export const Created = ({ created, access, labels, onDone }) => {
  const CREATED = labels.CREATED;
  const { credential, clientSecret, privateKey } = created;
  const signed = credential.method === API_CREDENTIAL_METHODS.PRIVATE_KEY;
  return (
    <div className="onboarding__stack">
      <div className="onboarding__banner onboarding__banner--success">
        <span className="onboarding__banner-icon" aria-hidden="true">
          <BiCheck size={20} />
        </span>
        <span className="onboarding__banner-text">
          <span className="onboarding__banner-title">
            {fill(CREATED.TITLE, { label: credential.label })}
          </span>
          <span>{signed ? CREATED.TEXT_KEY : CREATED.TEXT_SECRET}</span>
        </span>
      </div>
      {signed ? (
        <>
          <Value
            label={CREATED.USER_ID}
            value={credential.userId}
            labels={labels}
          />
          <Value
            label={CREATED.KEY_ID}
            value={credential.keyId}
            labels={labels}
          />
        </>
      ) : (
        <>
          <Value
            label={CREATED.CLIENT_ID}
            value={credential.clientId}
            labels={labels}
          />
          <Value
            label={CREATED.CLIENT_SECRET}
            value={clientSecret}
            labels={labels}
          />
        </>
      )}
      {privateKey ? (
        <div className="onboarding__card-actions">
          <p className="onboarding__success-line">
            <BiCheck size={16} aria-hidden="true" />
            {fill(CREATED.FILE_SAVED, { name: keyFileNameOf(credential) })}
          </p>
          <button
            type="button"
            className="onboarding__button onboarding__button--outline onboarding__button--small onboarding__button--icon"
            onClick={() => saveKeyFile({ credential, privateKey })}
          >
            <BiDownload size={15} />
            {CREATED.SAVE_AGAIN}
          </button>
        </div>
      ) : null}
      <p className="onboarding__notice onboarding__notice--warning onboarding__notice--compact">
        <BiError size={18} aria-hidden="true" />
        <span>{warningOf({ signed, privateKey }, CREATED)}</span>
      </p>
      <section className="onboarding__section onboarding__section--tight">
        <h3 className="onboarding__section-title">{CREATED.HOW}</h3>
        <p className="onboarding__paragraph">
          {signed
            ? fill(CREATED.HOW_KEY, {
                userId: credential.userId,
                issuer: access.issuer,
                keyId: credential.keyId,
              })
            : CREATED.HOW_SECRET}
        </p>
        <CodeBlock
          code={tokenCommandOf({ access, credential })}
          labels={labels}
        />
        <p className="onboarding__paragraph">
          {fill(CREATED.THEN, { apiUrl: access.apiUrl })}
        </p>
      </section>
      {onDone ? (
        <div className="onboarding__footer onboarding__footer--flat">
          <button
            type="button"
            className="onboarding__button onboarding__button--primary onboarding__push"
            onClick={onDone}
          >
            {CREATED.DONE}
          </button>
        </div>
      ) : null}
    </div>
  );
};

Created.propTypes = {
  created: PropTypes.object.isRequired,
  access: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  onDone: PropTypes.func,
};

Created.defaultProps = {
  onDone: null,
};

const ApiAccess = ({
  access,
  labels,
  busy,
  adminEmail,
  onToggle,
  onCreate,
  onDelete,
  onResend,
}) => {
  const [flow, setFlow] = useState(null);
  const [created, setCreated] = useState(null);
  const enabled = Boolean(access.enabled);
  const credentials = access.credentials || [];

  if (created && created.credential.pending)
    return (
      <Sent
        credential={created.credential}
        labels={labels}
        onDone={() => setCreated(null)}
      />
    );

  if (created)
    return (
      <Created
        created={created}
        access={access}
        labels={labels}
        onDone={() => setCreated(null)}
      />
    );

  if (enabled && flow && flow !== CHOOSE)
    return (
      <NewCredential
        method={flow}
        labels={labels}
        busy={busy}
        adminEmail={adminEmail}
        onCreate={onCreate}
        onBack={() => setFlow(isEmpty(credentials) ? null : CHOOSE)}
        onCreated={(result) => {
          setFlow(null);
          setCreated(result);
        }}
      />
    );

  const choosing = enabled && (isEmpty(credentials) || flow === CHOOSE);

  return (
    <div className="onboarding__stack">
      <div
        className={classNames("onboarding__card", {
          "onboarding__card--on": enabled,
        })}
      >
        <button
          type="button"
          aria-pressed={enabled}
          className="onboarding__toggle"
          disabled={busy}
          onClick={() => onToggle(!enabled)}
        >
          <span className="onboarding__switch" aria-hidden="true" />
          <span className="onboarding__toggle-text">
            <strong>{labels.SWITCH_TITLE}</strong>
            <span>{labels.SWITCH_TEXT}</span>
          </span>
        </button>
      </div>
      {enabled ? null : (
        <p className="onboarding__notice onboarding__notice--info">
          <BiPlug size={20} aria-hidden="true" />
          <span>{labels.OFF}</span>
        </p>
      )}
      {choosing ? (
        <>
          <h3 className="onboarding__section-title">{labels.CHOOSE_TITLE}</h3>
          <Choices labels={labels} onPick={setFlow} />
          {isEmpty(credentials) ? null : (
            <div className="onboarding__footer onboarding__footer--flat">
              <button
                type="button"
                className="onboarding__button onboarding__button--outline"
                onClick={() => setFlow(null)}
              >
                {labels.BACK}
              </button>
            </div>
          )}
        </>
      ) : null}
      {enabled && !choosing ? (
        <>
          <section className="onboarding__section onboarding__section--tight">
            <h3 className="onboarding__section-title">{labels.CREDENTIALS}</h3>
            <ul className="onboarding__credentials">
              {credentials.map((credential) => (
                <CredentialRow
                  key={credential.id}
                  credential={credential}
                  labels={labels}
                  busy={busy}
                  onDelete={onDelete}
                  onResend={onResend}
                />
              ))}
            </ul>
            <button
              type="button"
              className="onboarding__button onboarding__button--outline onboarding__button--icon onboarding__start"
              disabled={busy}
              onClick={() => setFlow(CHOOSE)}
            >
              <BiPlus size={16} />
              {labels.ADD}
            </button>
          </section>
          <section className="onboarding__section onboarding__section--tight">
            <h3 className="onboarding__section-title">{labels.CONNECTION}</h3>
            <Connection access={access} labels={labels} />
          </section>
        </>
      ) : null}
    </div>
  );
};

ApiAccess.propTypes = {
  access: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  busy: PropTypes.bool.isRequired,
  adminEmail: PropTypes.string,
  onToggle: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onResend: PropTypes.func.isRequired,
};

ApiAccess.defaultProps = {
  adminEmail: "",
};

export default ApiAccess;
